/////////////////////////////////////////////////////////
// Configurations related to listing.                  //
// Main configuration here is the extended data config //
/////////////////////////////////////////////////////////

import {
  LISTING_INPUT_METHOD,
  LISTING_CONDITION,
  LISTING_INTENT_TYPE,
  CONTRACT_LISTING_TYPE,
  LISTING_STATUS,
} from '../constants/listing';
import { LOCALES } from '../util/locale';
import { MARKETPLACE_ID } from '../util/marketplace';

// NOTE: if you want to change the structure of the data,
// you should also check src/util/configHelpers.js
// some validation is added there.

/**
 * Configuration options for listing fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be either 'public' or 'private'.
 *                                  Default value: 'public'.
 *                                  Note: listing doesn't support 'protected' scope atm.
 * - includeForListingTypes:        An array of listing types, for which the extended
 *   (optional)                     data is relevant and should be added.
 * - schemaType (optional):         Schema for this extended data field.
 *                                  This is relevant when rendering components and querying listings.
 *                                  Possible values: 'enum', 'multi-enum', 'text', 'long', 'boolean'.
 * - enumOptions (optional):        Options shown for 'enum' and 'multi-enum' extended data.
 *                                  These are used to render options for inputs and filters on
 *                                  EditListingPage, ListingPage, and SearchPage.
 * - filterConfig:                  Filter configuration for listings query.
 *    - indexForSearch (optional):    If set as true, it is assumed that the extended data key has
 *                                    search index in place. I.e. the key can be used to filter
 *                                    listing queries (then scope needs to be 'public').
 *                                    Note: Flex CLI can be used to set search index for the key:
 *                                    https://www.sharetribe.com/docs/references/extended-data/#search-schema
 *                                    Read more about filtering listings with public data keys from API Reference:
 *                                    https://www.sharetribe.com/api-reference/marketplace.html#extended-data-filtering
 *                                    Default value: false,
 *   - filterType:                    Sometimes a single schemaType can be rendered with different filter components.
 *                                    For 'enum' schema, filterType can be 'SelectSingleFilter' or 'SelectMultipleFilter'
 *   - label:                         Label for the filter, if the field can be used as query filter
 *   - searchMode (optional):         Search mode for indexed data with multi-enum schema.
 *                                    Possible values: 'has_all' or 'has_any'.
 *   - group:                         SearchPageWithMap has grouped filters. Possible values: 'primary' or 'secondary'.
 * - showConfig:                    Configuration for rendering listing. (How the field should be shown.)
 *   - label:                         Label for the saved data.
 *   - isDetail                       Can be used to hide detail row (of type enum, boolean, or long) from listing page.
 *                                    Default value: true,
 * - saveConfig:                    Configuration for adding and modifying extended data fields.
 *   - label:                         Label for the input field.
 *   - placeholderMessage (optional): Default message for user input.
 *   - isRequired (optional):         Is the field required for providers to fill
 *   - requiredMessage (optional):    Message for those fields, which are mandatory.
 */
export const listingFields = [
  {
    key: 'intentType',
    scope: 'public',
    schemaType: 'radio',
    marketplaceIds: [MARKETPLACE_ID.RESPARE],
    enumOptions: [
      { option: LISTING_INTENT_TYPE.SELLING, label: 'General.selling' },
      { option: LISTING_INTENT_TYPE.REQUESTING, label: 'General.requesting' },
    ],
    filterConfig: {
      indexForSearch: true,
      label: 'General.listingType',
      group: 'primary',
      filterType: 'SelectSingleFilter',
    },
    showConfig: {
      label: 'General.listingType',
      isDetail: true,
    },
    saveConfig: {
      label: 'General.listingType',
      isRequired: true,
      requiredMessage: 'General.optionRequired',
    },
  },
  {
    key: 'articleNumber',
    scope: 'public',
    schemaType: 'text',
    marketplaceIds: [MARKETPLACE_ID.RESPARE, MARKETPLACE_ID.SECOND_LIFE],
    showConfig: {
      label: 'General.articleNumber',
      isDetail: true,
    },
    saveConfig: {
      label: 'General.articleNumber',
      placeholderMessage: 'General.articleNumberPlaceholder',
      requiredMessage: 'General.fieldRequired',
    },
  },
  // This is only here so the fns. related to listingFields will work
  {
    key: 'inputMethod',
    scope: 'public',
    schemaType: 'enum',
    marketplaceIds: [MARKETPLACE_ID.RESPARE],
    enumOptions: [
      { option: LISTING_INPUT_METHOD.ARTICLE_NUMBER, label: '' },
      { option: LISTING_INPUT_METHOD.MANUAL, label: '' },
    ],
    saveConfig: { label: '' },
  },
  {
    key: 'category',
    scope: 'public',
    schemaType: 'enum',
    marketplaceIds: [MARKETPLACE_ID.RESPARE],
    enumOptions: [],
    filterConfig: {
      indexForSearch: false,
      label: 'General.category',
      group: 'primary',
      filterType: 'SelectMultipleFilter',
    },
    showConfig: {
      label: 'General.category',
      isDetail: true,
    },
    saveConfig: {
      label: 'General.category',
      placeholderMessage: 'General.categoryPlaceholder',
      requiredMessage: 'General.optionRequired',
    },
  },
  {
    key: 'subcategory',
    scope: 'public',
    schemaType: 'enum',
    marketplaceIds: [MARKETPLACE_ID.RESPARE],
    enumOptions: [],
    filterConfig: {
      indexForSearch: false,
      label: 'General.subcategory',
      group: 'primary',
      filterType: 'SelectMultipleFilter',
    },
    showConfig: {
      label: 'General.subcategory',
      isDetail: true,
    },
    saveConfig: {
      label: 'General.subcategory',
      placeholderMessage: 'General.subcategoryPlaceholder',
      requiredMessage: 'General.optionRequired',
    },
  },
  {
    key: 'itemType',
    scope: 'public',
    schemaType: 'enum',
    marketplaceIds: [MARKETPLACE_ID.RESPARE],
    enumOptions: [],
    filterConfig: {
      indexForSearch: false,
      label: 'General.itemType',
      group: 'primary',
      filterType: 'SelectMultipleFilter',
    },
    showConfig: {
      label: 'General.itemType',
      isDetail: true,
    },
    saveConfig: {
      label: 'General.itemType',
      placeholderMessage: 'General.itemTypePlaceholder',
      requiredMessage: 'General.optionRequired',
    },
  },
  {
    key: 'takenFromModel',
    scope: 'public',
    schemaType: 'text',
    marketplaceIds: [MARKETPLACE_ID.RESPARE],
    showConfig: {
      label: 'General.takenFromModel',
      isDetail: true,
    },
    saveConfig: {
      label: 'General.takenFromModel',
      placeholderMessage: 'General.modelPlaceholder',
    },
  },
  {
    key: 'matchesModel',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [],
    marketplaceIds: [MARKETPLACE_ID.RESPARE],
    showConfig: {
      label: 'General.matchesModels',
      isDetail: true,
    },
    saveConfig: {
      label: 'General.matchesModels',
      placeholderMessage: 'General.modelPlaceholder',
    },
  },
  {
    key: 'condition',
    scope: 'public',
    schemaType: 'enum',
    marketplaceIds: [MARKETPLACE_ID.RESPARE],
    enumOptions: [
      { option: LISTING_CONDITION.GRADE_A, label: 'General.gradeALong' },
      { option: LISTING_CONDITION.GRADE_B, label: 'General.gradeBLong' },
      { option: LISTING_CONDITION.GRADE_C, label: 'General.gradeCLong' },
      { option: LISTING_CONDITION.GRADE_D, label: 'General.gradeDLong' }, // US only
      { option: LISTING_CONDITION.NO_GRADE, label: 'General.noGrading' },
    ],
    filterConfig: {
      indexForSearch: true,
      label: 'General.condition',
      group: 'secondary',
      filterType: 'SelectSingleFilter',
    },
    showConfig: {
      label: 'General.condition',
      isDetail: true,
    },
    saveConfig: {
      label: 'General.condition',
      requiredMessage: 'General.optionRequired',
      isRequired: true,
    },
  },
  {
    key: 'additionalDetails',
    scope: 'public',
    schemaType: 'text',
    marketplaceIds: [MARKETPLACE_ID.RESPARE],
    showConfig: {
      label: 'General.additionalDetails',
      isDetail: true,
    },
    saveConfig: {
      label: 'General.additionalDetails',
      placeholderMessage: 'General.additionalDetailsPlaceholder',
    },
  },
  {
    key: 'status',
    scope: 'meta',
    schemaType: 'enum',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    enumOptions: [
      {
        option: LISTING_STATUS.PENDING_INTAKE,
        label: `General.${LISTING_STATUS.PENDING_INTAKE}.label`,
        subFilters: [
          { label: 'General.soon', value: 'soon' },
          { label: 'General.expired', value: 'expired' },
        ],
      },
      {
        option: LISTING_STATUS.PENDING_REVIEW,
        label: `General.${LISTING_STATUS.PENDING_REVIEW}.label`,
        subFilters: [],
      },
      // for Admins:
      {
        option: LISTING_STATUS.SELL_AS_IS,
        label: `General.${LISTING_STATUS.SELL_AS_IS}.label`,
        subFilters: [],
      },
      // for Dealers:
      {
        option: LISTING_STATUS.SELLING,
        label: `General.${LISTING_STATUS.SELL_AS_IS}.label`,
        subFilters: [],
      },
      {
        option: LISTING_STATUS.SET_FOR_REFURBISHMENT,
        label: `General.${LISTING_STATUS.SET_FOR_REFURBISHMENT}.label`,
        subFilters: [],
      },
      {
        option: LISTING_STATUS.REFURBISHED,
        label: `General.${LISTING_STATUS.REFURBISHED}.label`,
        subFilters: [],
      },
      {
        option: LISTING_STATUS.PUBLISHED,
        label: `General.${LISTING_STATUS.PUBLISHED}.label`,
        subFilters: [],
      },
      {
        option: LISTING_STATUS.ARCHIVED,
        label: 'General.archived',
        subFilters: [],
      },
    ],
    filterConfig: {
      indexForSearch: true,
      label: 'General.leaseStatus',
      group: 'primary',
      filterType: 'SelectSingleFilter',
    },
    showConfig: {
      label: 'General.leaseStatus',
      isDetail: false,
    },
    saveConfig: {
      label: 'General.leaseStatus',
      requiredMessage: 'General.optionRequired',
      isRequired: false,
    },
  },
  {
    key: 'schemaImageUrls',
    scope: 'public',
    schemaType: 'text',
    marketplaceIds: [MARKETPLACE_ID.RESPARE],
    saveConfig: { label: '' },
  },
  {
    key: 'suggestionsInput',
    scope: 'public',
    schemaType: 'text',
    marketplaceIds: [MARKETPLACE_ID.RESPARE],
    saveConfig: { label: '' },
  },
  {
    key: 'country',
    scope: 'public',
    schemaType: 'enum',
    marketplaceIds: [MARKETPLACE_ID.RESPARE],
    enumOptions: [],
    filterConfig: {
      indexForSearch: true,
      label: 'General.country',
      group: 'primary',
      filterType: 'SelectMultipleFilter',
    },
    saveConfig: { label: '' },
  },
  {
    key: 'contractId',
    scope: 'public',
    schemaType: 'text',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    filterConfig: {
      indexForSearch: false,
      label: 'General.contractId',
      group: 'primary',
    },
    saveConfig: { label: '' },
    showConfig: {
      label: 'General.contractId',
      isDetail: false,
    },
  },
  {
    key: 'contractStartDate',
    scope: 'public',
    schemaType: 'text',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    filterConfig: {
      indexForSearch: false,
      label: 'General.contractStartDate',
      group: 'primary',
    },
    saveConfig: { label: '' },
    showConfig: {
      label: 'General.contractStartDate',
      isDetail: false,
    },
  },
  {
    key: 'contractEndDate',
    scope: 'public',
    schemaType: 'text',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    filterConfig: {
      indexForSearch: false,
      label: 'General.contractEndDate',
      group: 'primary',
    },
    saveConfig: { label: '' },
    showConfig: {
      label: 'General.contractEndDate',
      isDetail: false,
    },
  },
  {
    key: 'runningHours',
    scope: 'public',
    schemaType: 'long',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    filterConfig: {
      indexForSearch: false,
      label: 'General.runningHours',
      group: 'primary',
    },
    saveConfig: { label: '' },
    showConfig: {
      label: 'General.runningHours',
      isDetail: true,
    },
  },
  {
    key: 'sellingDealerId',
    scope: 'public',
    schemaType: 'text',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    filterConfig: {
      indexForSearch: false,
      label: 'General.sellingDealer',
      group: 'primary',
    },
    saveConfig: { label: '' },
    showConfig: {
      label: 'General.sellingDealer',
      isDetail: true,
    },
  },
  {
    key: 'contractEndOwnerId',
    scope: 'public',
    schemaType: 'text',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    filterConfig: {
      indexForSearch: false,
      label: 'General.contractEndOwner',
      group: 'primary',
    },
    saveConfig: { label: '' },
    showConfig: {
      label: 'General.contractEndOwner',
      isDetail: true,
    },
  },

  {
    key: 'productServiced',
    scope: 'public',
    schemaType: 'enum',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    enumOptions: [
      { option: 'no', label: 'General.no' },
      { option: 'yes', label: 'General.yes' },
    ],
    filterConfig: {
      indexForSearch: false,
      label: 'General.productServiced',
      group: 'primary',
    },
    saveConfig: { label: '' },
    showConfig: {
      label: 'General.productServiced',
      isDetail: true,
    },
  },
  {
    key: 'productTestReport',
    scope: 'public',
    schemaType: 'text',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    filterConfig: {
      indexForSearch: false,
      label: 'General.productTestReport',
      group: 'primary',
    },
    saveConfig: { label: '' },
    showConfig: {
      label: 'General.productTestReport',
      isDetail: false,
    },
  },
  {
    key: 'refurbishDetails',
    scope: 'public',
    schemaType: 'text',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    filterConfig: {
      indexForSearch: false,
      label: 'General.refurbishDetails',
      group: 'primary',
    },
    saveConfig: { label: '' },
    showConfig: {
      label: 'General.refurbishDetails',
      isDetail: true,
    },
  },
  {
    key: 'productTestReportAfterRefurbish',
    scope: 'public',
    schemaType: 'text',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    filterConfig: {
      indexForSearch: false,
      label: 'General.productTestReportAfterRefurbish',
      group: 'primary',
    },
    saveConfig: { label: '' },
    showConfig: {
      label: 'General.productTestReportAfterRefurbish',
      isDetail: true,
    },
  },
  {
    key: 'serviceChecklist',
    scope: 'public',
    schemaType: 'text',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    filterConfig: {
      indexForSearch: false,
      label: 'General.serviceChecklist',
      group: 'primary',
    },
    saveConfig: { label: '' },
    showConfig: {
      label: 'General.serviceChecklist',
      isDetail: true,
    },
  },
  {
    key: 'offerForSaleDetails',
    scope: 'public',
    schemaType: 'text',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    filterConfig: {
      indexForSearch: false,
      label: 'General.offerForSaleDetails',
      group: 'primary',
    },
    saveConfig: { label: '' },
    showConfig: {
      label: 'General.offerForSaleDetails',
      isDetail: true,
    },
  },
  {
    key: 'reviewDetails',
    scope: 'public',
    schemaType: 'text',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    filterConfig: {
      indexForSearch: false,
      label: 'General.reviewDetails',
      group: 'primary',
    },
    saveConfig: { label: '' },
    showConfig: {
      label: 'General.reviewDetails',
      isDetail: true,
    },
  },
  {
    key: 'soldAt',
    scope: 'public',
    schemaType: 'text',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    filterConfig: {
      indexForSearch: false,
      label: 'General.dateOfSelling',
      group: 'primary',
    },
    saveConfig: { label: '' },
    showConfig: {
      label: 'General.dateOfSelling',
      isDetail: true,
    },
  },
  {
    key: 'priceSold',
    scope: 'public',
    schemaType: 'long',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    filterConfig: {
      indexForSearch: false,
      label: 'General.price',
      group: 'primary',
    },
    saveConfig: { label: '' },
  },
  {
    key: 'dimrProductImageStudio',
    scope: 'public',
    schemaType: 'text',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    saveConfig: { label: '' },
  },
  {
    key: 'inputGrading',
    scope: 'public',
    schemaType: 'enum',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    enumOptions: [
      { option: LISTING_CONDITION.GRADE_A, label: 'General.gradeA' },
      { option: LISTING_CONDITION.GRADE_B, label: 'General.gradeB' },
      { option: LISTING_CONDITION.GRADE_C, label: 'General.gradeC' },
      { option: LISTING_CONDITION.GRADE_D, label: 'General.gradeD' },
    ],
    filterConfig: {
      indexForSearch: true,
      label: 'General.inputGrade',
      group: 'primary',
      filterType: 'SelectSingleFilter',
    },
    showConfig: {
      label: 'General.inputGrade',
      isDetail: false,
      isVisible: false,
    },
    saveConfig: {
      label: '',
    },
  },
  {
    key: 'outputGrading',
    scope: 'public',
    schemaType: 'enum',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    enumOptions: [
      { option: LISTING_CONDITION.GRADE_A, label: 'General.gradeA' },
      { option: LISTING_CONDITION.GRADE_B, label: 'General.gradeB' },
      { option: LISTING_CONDITION.GRADE_C, label: 'General.gradeC' },
      { option: LISTING_CONDITION.GRADE_D, label: 'General.gradeD' },
    ],
    filterConfig: {
      indexForSearch: true,
      label: 'General.outputGrade',
      group: 'primary',
      filterType: 'SelectSingleFilter',
    },
    showConfig: {
      label: 'General.outputGrade',
      isDetail: false,
      isVisible: false,
    },
    saveConfig: {
      label: '',
    },
  },
  {
    key: 'dimrModel',
    scope: 'public',
    schemaType: 'enum',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    enumOptions: [],
    filterConfig: {
      indexForSearch: true,
      label: 'General.dimrModel',
      group: 'primary',
      filterType: 'SelectSingleFilter',
    },
    showConfig: {
      label: 'General.dimrModel',
      isDetail: false,
    },
    saveConfig: {
      label: '',
    },
  },
  {
    key: 'husqvarnaCertified',
    scope: 'public',
    schemaType: 'enum',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    enumOptions: [
      { option: 'true', label: 'General.yes' },
      { option: 'false', label: 'General.no' },
    ],
    filterConfig: {
      indexForSearch: true,
      label: 'General.certified',
      group: 'primary',
      filterType: 'SelectSingleFilter',
    },
    saveConfig: { label: '' },
    showConfig: {
      label: 'General.certified',
      isDetail: true,
    },
  },
  {
    key: 'contractEndOwnerCompanyId',
    scope: 'meta',
    schemaType: 'enum',
    marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
    enumOptions: [],
    filterConfig: {
      indexForSearch: true,
      label: 'General.dealer',
      group: 'primary',
      filterType: 'SelectSingleFilter',
    },
    showConfig: {
      label: 'General.dealer',
      isDetail: false,
    },
    saveConfig: {
      label: '',
    },
  },
  {
    key: 'locale',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: LOCALES.map(locale => ({
      option: locale,
      label: `General.locale${locale.toUpperCase()}`,
    })),
    marketplaceIds: [MARKETPLACE_ID.RESPARE, MARKETPLACE_ID.SECOND_LIFE],
    filterConfig: {
      indexForSearch: true,
      label: 'General.country',
      group: 'primary',
      filterType: 'SelectSingleFilter',
    },
    saveConfig: { label: '' },
  },
  // {
  //   key: 'residualValue',
  //   scope: 'public',
  //   schemaType: 'long',
  //   marketplaceIds: [MARKETPLACE_ID.SECOND_LIFE],
  //   filterConfig: {
  //     indexForSearch: true,
  //     label: 'General.price',
  //     group: 'primary',
  //     min: 0,
  //     max: 10000,
  //   },
  //   showConfig: {
  //     label: 'General.price',
  //     isDetail: false,
  //   },
  //   saveConfig: { label: '' },
  //   priceConfig: {
  //     priceValues: { min: 0, max: 10000, step: 100 },
  //   },
  // },

  // // An example of how to use transaction type specific custom fields and private data.
  // {
  //   key: 'note',
  //   scope: 'public',
  //   includeForListingTypes: ['product-selling'],
  //   schemaType: 'text',
  //   showConfig: {
  //     label: 'Extra notes',
  //   },
  //   saveConfig: {
  //     label: 'Extra notes',
  //     placeholderMessage: 'Some public extra note about this bike...',
  //   },
  // },
  // {
  //   key: 'privatenote',
  //   scope: 'private',
  //   includeForListingTypes: ['daily-booking'],
  //   schemaType: 'text',
  //   saveConfig: {
  //     label: 'Private notes',
  //     placeholderMessage: 'Some private note about this bike...',
  //   },
  // },
];

///////////////////////////////////////////////////////////////////////
// Configurations related to listing types and transaction processes //
///////////////////////////////////////////////////////////////////////

// A presets of supported listing configurations
//
// Note 1: With first iteration of hosted configs, we are unlikely to support
//         multiple listing types, even though this template has some
//         rudimentary support for it.
// Note 2: transaction type is part of listing type. It defines what transaction process and units
//         are used when transaction is created against a specific listing.

/**
 * Configuration options for listing experience:
 * - listingType:     Unique string. This will be saved to listing's public data on
 *                    EditListingWizard.
 * - label            Label for the listing type. Used as microcopy for options to select
 *                    listing type in EditListingWizard.
 * - transactionType  Set of configurations how this listing type will behave when transaction is
 *                    created.
 *   - process          Transaction process.
 *                      The process must match one of the processes that this client app can handle
 *                      (check src/util/transaction.js) and the process must also exists in correct
 *                      marketplace environment.
 *   - alias            Valid alias for the aforementioned process. This will be saved to listing's
 *                      public data as transctionProcessAlias and transaction is initiated with this.
 *   - unitType         Unit type is mainly used as pricing unit. This will be saved to
 *                      transaction's protected data.
 *                      Recommendation: don't use same unit types in completely different processes
 *                      ('item' sold should not be priced the same as 'item' booked).
 * - stockType        This is relevant only to listings with product-selling listing type.
 *                    If set to 'oneItem', stock management is not showed and the listing is
 *                    considered unique (stock = 1).
 *                    Possible values: 'oneItem' and 'multipleItems'.
 *                    Default: 'multipleItems'.
 */

export const listingTypes = [
  // {
  //   listingType: 'daily-booking',
  //   label: 'Daily booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'day',
  //   },
  // },
  // // Here are some examples for other listingTypes
  // // TODO: SearchPage does not work well if both booking and product selling are used at the same time
  // {
  //   listingType: 'nightly-booking',
  //   label: 'Nightly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'night',
  //   },
  // },
  // {
  //   listingType: 'hourly-booking',
  //   label: 'Hourly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'hour',
  //   },
  // },
  {
    listingType: 'product-selling',
    label: 'Sell parts',
    transactionType: {
      process: 'default-purchase',
      alias: 'default-purchase/release-1',
      unitType: 'item',
    },
    stockType: 'multipleItems',
  },
  {
    listingType: 'inquiry',
    label: 'Inquiry',
    transactionType: {
      process: 'default-inquiry',
      alias: 'default-inquiry/release-1',
      unitType: 'inquiry',
    },
    defaultListingFields: {
      price: false,
    },
  },
  {
    listingType: CONTRACT_LISTING_TYPE,
    label: 'Contract lease',
    transactionType: {
      process: CONTRACT_LISTING_TYPE,
      alias: `${CONTRACT_LISTING_TYPE}/release-1`,
      unitType: 'item',
    },
    stockType: 'multipleItems',
  },
];

// SearchPage can enforce listing query to only those listings with valid listingType
// However, it only works if you have set 'enum' type search schema for the public data fields
//   - listingType
//
//  Similar setup could be expanded to 2 other extended data fields:
//   - transactionProcessAlias
//   - unitType
//
// Read More:
// https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
export const enforceValidListingType = false;
